import React, { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, Table, Tag, Tooltip, Typography, message } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { getCustomVoices, deleteCustomVoice, AdminVoiceSlot } from '../../../../Actions/adminSlotApi'

type Props = {
  workspaceId: number
}

export default function CustomVoiceSlots({ workspaceId }: Props) {
  const queryClient = useQueryClient()
  const [messageApi, contextHolder] = message.useMessage()

  const queryKey = ['adminCustomVoices', workspaceId]

  const { data: slots = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => getCustomVoices(workspaceId),
    enabled: !!workspaceId,
  })

  const deleteMutation = useMutation({
    mutationFn: (voiceConversionId: number) => deleteCustomVoice(voiceConversionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      messageApi.success('커스텀 보이스 슬롯을 삭제했습니다.')
    },
    onError: (e: Error) => messageApi.error(e.message),
  })

  const handleDelete = useCallback(
    (record: AdminVoiceSlot) => {
      Modal.confirm({
        title: '커스텀 보이스 슬롯 삭제',
        content: `"${record.name || `ID: ${record.voiceConversionId}`}" 보이스를 삭제하시겠습니까? 워크스페이스에서 즉시 사라지며, 복구는 관리자 DB 작업이 필요합니다.`,
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: () => deleteMutation.mutate(record.voiceConversionId),
      })
    },
    [deleteMutation],
  )

  const columns = [
    {
      title: 'ID',
      dataIndex: 'voiceConversionId',
      key: 'voiceConversionId',
      width: 70,
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      render: (text: string | null) => text || '-',
    },
    {
      title: '샘플',
      dataIndex: 'sampleUrl',
      key: 'sampleUrl',
      width: 260,
      render: (url: string | null) =>
        url ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio controls src={url} style={{ height: 32, maxWidth: 240 }} />
        ) : (
          <Typography.Text type="secondary">생성 중</Typography.Text>
        ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string | null) => (status ? <Tag>{status}</Tag> : '-'),
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text: string | null) => (text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '삭제여부',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 160,
      render: (text: string | null) =>
        text ? <Tag color="default">삭제됨 · {dayjs(text).format('YYYY-MM-DD HH:mm')}</Tag> : <Tag color="green">활성</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, record: AdminVoiceSlot) =>
        record.deletedAt ? (
          <Tooltip title="이미 삭제된 슬롯">
            <Button type="text" danger icon={<DeleteOutlined />} disabled />
          </Tooltip>
        ) : (
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        ),
    },
  ]

  return (
    <div style={{ marginBottom: 20 }}>
      {contextHolder}
      <Typography.Title level={4}>커스텀 보이스</Typography.Title>
      <Table
        columns={columns}
        dataSource={slots}
        rowKey="voiceConversionId"
        loading={isLoading}
        pagination={false}
        onRow={(record: AdminVoiceSlot) => (record.deletedAt ? { style: { opacity: 0.5 } } : {})}
        locale={{ emptyText: '커스텀 보이스가 없습니다.' }}
      />
    </div>
  )
}
